import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Dropdown, Menu, message } from 'antd';
import React, { useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import UploadWithSampleModal from '../flatfile/UploadWithSampleModal';
import { REGEX_CONFIG_NAMES, regexRecordHook } from '../flatfile/faltfile_utils';
import { budgetsRestCrudThunksForBuyer, getBudgetCycleForBuyer } from '../../thunks/budget_thunks';
import { workOrdersRestCrudThunksForBuyer } from '../../thunks/work_orders_thunks';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	parseFilterValueToAdvancedFilters,
} from '../../utils/DataAccessUtils';
import { ExportToCsv } from 'export-to-csv';
import { CSV_EXPORT_DEFAULTS } from '../../utils/DataConstants';
import FlatfileImporter from '../flatfile/FlatfileImporter';

const MENU_KEYS = {
	NON_OPENWRENCH: 'External Payments - Non-OpenWrench WOs',
	OPENWRENCH: 'External Payments - OpenWrench WOs',
};

const SHARED_INVOICE_FIELDS = [
	{
		label: 'Invoice Date (MM/DD/YYYY)',
		key: 'invoiceDate',
		type: 'string',
		constraints: [{ type: 'required' }],
	},
	{
		label: 'Invoice Amount',
		key: 'invoiceAmount',
		type: 'number',
		constraints: [{ type: 'required' }],
	},
];

const NON_OPENWRENCH_FIELDS = SHARED_INVOICE_FIELDS;

const OPEN_WRENCH_FIELDS = [
	{
		label: 'Work Order #',
		key: 'workOrderId',
		type: 'number',
		constraints: [{ type: 'required' }, { type: 'unique' }],
	},
	...SHARED_INVOICE_FIELDS,
];

const normalizeNonOpenWrenchRecords = (records = []) => ({
	workOrdersManagedOutside: records.map((record) => ({
		invoiceAmount: parseFloat(record.invoiceAmount),
		invoiceDate: record.invoiceDate,
	})),
});

const normalizeOpenWrenchRecords = (records = []) => ({
	workOrderOverrides: records.map((record) => ({
		workOrderId: parseInt(record.workOrderId, 10),
		invoiceAmount: parseFloat(record.invoiceAmount),
		invoiceDate: record.invoiceDate,
	})),
});

const invoiceRecordHook = regexRecordHook([
	REGEX_CONFIG_NAMES.INVOICE_DATE,
	REGEX_CONFIG_NAMES.NUMBER_VALUE,
]);

const BudgetAddExceptions = ({
	budget,
	uploadExternalPayments,
	fetchWorkOrdersByIds,
	fetchBudgetCycle,
}: any) => {
	const budgetId = budget ? budget.id : undefined;

	const [activeImporterKey, setActiveImporterKey] = useState(null);
	const [uploadModalVisible, setUploadModalVisible] = useState(false);
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [downloadingSample, setDownloadingSample] = useState(false);

	const onUploadClick = useCallback(
		({ key }) => {
			if (!budgetId) {
				return;
			}
			if (key === MENU_KEYS.NON_OPENWRENCH || key === MENU_KEYS.OPENWRENCH) {
				setActiveImporterKey(key);
				setUploadModalVisible(true);
			}
		},
		[budgetId]
	);

	const onEditClick = useCallback(
		({ key }) => {
			if (!budgetId) {
				return;
			}
			if (key === MENU_KEYS.NON_OPENWRENCH || key === MENU_KEYS.OPENWRENCH) {
				setActiveImporterKey(key);
				setEditModalVisible(true);
			}
		},
		[budgetId]
	);

	const onUploadEditCancel = useCallback(() => {
		setUploadModalVisible(false);
		setEditModalVisible(false);
		setDownloadingSample(false);
		setActiveImporterKey(null);
	}, [setUploadModalVisible, setEditModalVisible, setDownloadingSample, setActiveImporterKey]);

	const onNonOpenWrenchDataLoad = useCallback(
		({ validData }) => {
			const normalizedRecords = normalizeNonOpenWrenchRecords(validData);
			const hasInvalidAmounts = normalizedRecords.workOrdersManagedOutside.some((record) =>
				isNaN(record.invoiceAmount)
			);

			if (hasInvalidAmounts) {
				return Promise.reject('Invoice Amount must be a valid number.');
			}

			return uploadExternalPayments(budgetId, normalizedRecords).then(() => {
				message.success('External payments uploaded successfully.');
			});
		},
		[budgetId, uploadExternalPayments]
	);

	const onOpenWrenchDataLoad = useCallback(
		({ validData }) => {
			const normalizedRecords = normalizeOpenWrenchRecords(validData);
			const hasInvalidAmounts = normalizedRecords.workOrderOverrides.some((record) =>
				isNaN(record.invoiceAmount)
			);

			if (hasInvalidAmounts) {
				return Promise.reject('Invoice Amount must be a valid number.');
			}

			const workOrderIds = normalizedRecords.workOrderOverrides.map((record) => record.workOrderId);

			return fetchWorkOrdersByIds(
				parseFilterValueToAdvancedFilters({
					isCapex: budget.isCapex,
					isPM: budget.isPM,
					ids: workOrderIds,
					problemTypeIds: nullSafeGet('problemTypeIds', budget),
					locationIds: nullSafeGet('locationIds', budget),
					supplierFacilityIds: nullSafeGet('supplierFacilityIds', budget),
				})
			).then((existingWorkOrders) => {
				const existingIds = new Set(
					existingWorkOrders
						.map((workOrder) => Number(nullSafeGet('source.id', workOrder)))
						.filter((id) => !isNaN(id))
				);
				const missingIds = workOrderIds.filter((id) => !existingIds.has(id));

				if (missingIds.length > 0) {
					const missingIdSet = new Set(missingIds);
					const failedRows = normalizedRecords.workOrderOverrides
						.map((record, index) => ({
							id: record.workOrderId,
							row: index + 1, // +1 because row 0 is header, data starts at row 1
						}))
						.filter((item) => missingIdSet.has(item.id));

					const errorDetails = failedRows.map((item) => `${item.id} (Row ${item.row})`).join(', ');

					return Promise.reject(
						`Work Order # not found for the given budget criteria: ${errorDetails}`
					);
				}
				const currentCycle = nullSafeGetOrElse('cycle', budget, '');
				return uploadExternalPayments(budgetId, normalizedRecords)
					.then(() => {
						message.success('External payments uploaded successfully.');
						if (currentCycle) {
							fetchBudgetCycle(budgetId, { cycle: currentCycle });
						}
					})
					.catch((error) => {
						message.error(error || 'Error uploading external payments.');
					});
			});
		},
		[budgetId, fetchWorkOrdersByIds, uploadExternalPayments, fetchBudgetCycle, budget]
	);

	const downloadNonOpenWrenchSample = useCallback(() => {
		setDownloadingSample(true);
		try {
			const csvTitle = 'external_payments_non_openwrench';
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: NON_OPENWRENCH_FIELDS.map((field) => field.label),
			});

			const existingRecords = nullSafeGetOrElse('workOrdersManagedOutside', budget, []);
			const data =
				existingRecords.length > 0
					? existingRecords.map((record) => ({
							invoiceDate: nullSafeGetOrElse('invoiceDate', record, ''),
							invoiceAmount: nullSafeGetOrElse('invoiceAmount', record, ''),
					  }))
					: [
							{
								invoiceDate: '01/01/2024',
								invoiceAmount: '500',
							},
					  ];

			csvExporter.generateCsv(data);
		} finally {
			setDownloadingSample(false);
		}
	}, [budget]);

	const downloadOpenWrenchSample = useCallback(() => {
		setDownloadingSample(true);
		try {
			const csvTitle = 'external_payments_openwrench';
			const csvExporter = new ExportToCsv({
				...CSV_EXPORT_DEFAULTS,
				filename: csvTitle,
				title: csvTitle,
				headers: OPEN_WRENCH_FIELDS.map((field) => field.label),
			});

			const existingRecords = nullSafeGetOrElse('workOrderOverrides', budget, []);
			const data =
				existingRecords.length > 0
					? existingRecords.map((record) => ({
							workOrderId: nullSafeGetOrElse('workOrderId', record, ''),
							invoiceDate: nullSafeGetOrElse('invoiceDate', record, ''),
							invoiceAmount: nullSafeGetOrElse('invoiceAmount', record, ''),
					  }))
					: [
							{
								workOrderId: 123456,
								invoiceDate: '01/01/2024',
								invoiceAmount: '500',
							},
					  ];

			csvExporter.generateCsv(data);
		} finally {
			setDownloadingSample(false);
		}
	}, [budget]);

	const activeImporterConfig = useMemo(() => {
		if (activeImporterKey === MENU_KEYS.NON_OPENWRENCH) {
			return {
				type: 'external_payments_non_openwrench',
				title: 'Upload External Payments - Non-OpenWrench WOs',
				entityName: 'External Payments',
				sampleFileText: 'Download External Payments',
				downloadText: ' ',
				onDownloadSample: downloadNonOpenWrenchSample,
				fields: NON_OPENWRENCH_FIELDS,
				onDataLoad: onNonOpenWrenchDataLoad,
				data: nullSafeGetOrElse('workOrdersManagedOutside', budget, []),
			};
		}
		if (activeImporterKey === MENU_KEYS.OPENWRENCH) {
			return {
				type: 'external_payments_openwrench',
				title: 'Upload External Payments - OpenWrench WOs',
				entityName: 'External Payments',
				sampleFileText: 'Download External Payments',
				downloadText: ' ',
				onDownloadSample: downloadOpenWrenchSample,
				fields: OPEN_WRENCH_FIELDS,
				onDataLoad: onOpenWrenchDataLoad,
				data: nullSafeGetOrElse('workOrderOverrides', budget, []),
			};
		}
		return null;
	}, [
		activeImporterKey,
		downloadNonOpenWrenchSample,
		downloadOpenWrenchSample,
		onNonOpenWrenchDataLoad,
		onOpenWrenchDataLoad,
		budget,
	]);

	const menu = useMemo(
		() => (
			<Menu>
				<Menu.Item key={MENU_KEYS.NON_OPENWRENCH}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<span style={{ flex: 1 }}>External Payments- Non-OpenWrench WOs</span>
						<Button type="link" onClick={() => onEditClick({ key: MENU_KEYS.NON_OPENWRENCH })}>
							Edit
						</Button>
						<Button
							type="default"
							icon={<UploadOutlined />}
							onClick={() => onUploadClick({ key: MENU_KEYS.NON_OPENWRENCH })}
						>
							Upload
						</Button>
					</div>
				</Menu.Item>
				<Menu.Item key={MENU_KEYS.OPENWRENCH}>
					<div style={{ display: 'flex', alignItems: 'center' }}>
						<span style={{ flex: 1 }}>External Payments- OpenWrench WOs</span>
						<Button type="link" onClick={() => onEditClick({ key: MENU_KEYS.OPENWRENCH })}>
							Edit
						</Button>
						<Button
							type="default"
							icon={<UploadOutlined />}
							onClick={() => onUploadClick({ key: MENU_KEYS.OPENWRENCH })}
						>
							Upload
						</Button>
					</div>
				</Menu.Item>
			</Menu>
		),
		[]
	);

	return (
		<>
			<Dropdown overlay={menu} trigger={['click']}>
				<Button disabled={!budgetId}>
					<PlusOutlined />
					Add Exception
				</Button>
			</Dropdown>
			{uploadModalVisible && activeImporterConfig ? (
				<UploadWithSampleModal
					key={`${activeImporterConfig.type}`}
					type={activeImporterConfig.type}
					entityName={activeImporterConfig.entityName}
					title={activeImporterConfig.title}
					downloadText={activeImporterConfig.downloadText}
					sampleFileText={activeImporterConfig.sampleFileText}
					onCancel={onUploadEditCancel}
					onDownloadSample={activeImporterConfig.onDownloadSample}
					downloading={downloadingSample}
					fields={activeImporterConfig.fields}
					onDataLoad={activeImporterConfig.onDataLoad}
					recordHook={invoiceRecordHook}
					showWarningMessage
				/>
			) : null}
			{editModalVisible && activeImporterConfig ? (
				<FlatfileImporter
					dataLoadComplete={editModalVisible}
					type={activeImporterConfig.type}
					fields={activeImporterConfig.fields}
					data={activeImporterConfig.data}
					onComplete={onUploadEditCancel}
					onDataLoad={activeImporterConfig.onDataLoad}
				/>
			) : null}
		</>
	);
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) => ({
	uploadExternalPayments: (budgetId, records) =>
		dispatch(budgetsRestCrudThunksForBuyer.updatePartially(budgetId, records)),
	fetchWorkOrdersByIds: (params) =>
		dispatch(
			workOrdersRestCrudThunksForBuyer.searchWithPostMethod(params, null, null, null, null, false)
		),
	fetchBudgetCycle: (budgetId, params) => dispatch(getBudgetCycleForBuyer(budgetId, params)),
});

export default connect<any, any, any, any>(
	mapStateToProps,
	mapDispatchToProps
)(BudgetAddExceptions as any) as any;
